import type { ReactNode } from 'react';

/**
 * The three status cards that used to be /HowItWorks.svg.
 *
 * The artwork was drawn on a 528x460 canvas, so every size here is expressed in
 * those units: the root sets 1em = 1 canvas unit via container query units,
 * which lets the whole card scale as one piece exactly like the SVG did.
 */
const CANVAS_WIDTH = 528;

/** Icon paths are lifted verbatim from the original file; each viewBox crops the
 *  24x24 region that icon occupied on the full canvas, so no coordinates change. */
const StatusIcon = ({ viewBox, children }: { viewBox: string; children: ReactNode }) => (
    <svg
        viewBox={viewBox}
        fill="none"
        stroke="white"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={{ width: '24em', height: '24em' }}
    >
        {children}
    </svg>
);

const ScanIcon = () => (
    <StatusIcon viewBox="68 68 24 24">
        <path d="M85.25 70.375H89.625V74.75" />
        <path d="M74.75 89.625H70.375V85.25" />
        <path d="M89.625 85.25V89.625H85.25" />
        <path d="M70.375 74.75V70.375H74.75" />
        <path d="M84.375 74.75H75.625C75.1418 74.75 74.75 75.1418 74.75 75.625V84.375C74.75 84.8582 75.1418 85.25 75.625 85.25H84.375C84.8582 85.25 85.25 84.8582 85.25 84.375V75.625C85.25 75.1418 84.8582 74.75 84.375 74.75Z" />
    </StatusIcon>
);

const CpuIcon = () => (
    <StatusIcon viewBox="68 216 24 24">
        <path d="M83.0625 224.938H76.9375V231.062H83.0625V224.938Z" />
        <path d="M87.875 219.25H72.125C71.6418 219.25 71.25 219.642 71.25 220.125V235.875C71.25 236.358 71.6418 236.75 72.125 236.75H87.875C88.3582 236.75 88.75 236.358 88.75 235.875V220.125C88.75 219.642 88.3582 219.25 87.875 219.25Z" />
        <path d="M88.75 225.375H91.375" />
        <path d="M88.75 230.625H91.375" />
        <path d="M68.625 225.375H71.25" />
        <path d="M68.625 230.625H71.25" />
        <path d="M82.625 236.75V239.375" />
        <path d="M77.375 236.75V239.375" />
        <path d="M82.625 216.625V219.25" />
        <path d="M77.375 216.625V219.25" />
    </StatusIcon>
);

const NoteIcon = () => (
    <StatusIcon viewBox="68 344 24 24">
        <path d="M76.5 352.5H83.5" />
        <path d="M76.5 356H83.5" />
        <path d="M76.5 359.5H80" />
        <path d="M83.1391 365.625H71.25C71.0179 365.625 70.7954 365.533 70.6313 365.369C70.4672 365.205 70.375 364.982 70.375 364.75V347.25C70.375 347.018 70.4672 346.795 70.6313 346.631C70.7954 346.467 71.0179 346.375 71.25 346.375H88.75C88.9821 346.375 89.2046 346.467 89.3687 346.631C89.5328 346.795 89.625 347.018 89.625 347.25V359.139C89.6254 359.253 89.6034 359.365 89.5602 359.47C89.5171 359.575 89.4536 359.671 89.3734 359.752L83.7516 365.373C83.671 365.454 83.5755 365.517 83.4704 365.56C83.3653 365.603 83.2527 365.625 83.1391 365.625V365.625Z" />
        <path d="M89.5484 359.5H83.5V365.548" />
    </StatusIcon>
);

/** Every card carries a progress bar, so all three share card 1's original height. */
const CARD_HEIGHT = 120;

const cards = [
    {
        title: 'Question Scanned',
        subtitle: 'Processing...',
        icon: <ScanIcon />,
    },
    {
        title: 'Analyzing Input',
        subtitle: 'Decoding patterns',
        icon: <CpuIcon />,
    },
    {
        title: 'Final Result',
        subtitle: 'Synthesized. Optimized. Ready.',
        icon: <NoteIcon />,
    },
];

/** Dimmed treatment for steps that have not activated yet — same values as
 *  `.processing-dull` in globals.css, the project's existing inactive look. */
const DIMMED = '[filter:brightness(0.4)_saturate(0.5)]';

type HowItWorksProgressProps = {
    /** -1 = not started; index of the step currently filling; > index = completed */
    activeStep: number;
    /** Called when the active step's bar finishes filling */
    onStepComplete: () => void;
};

const HowItWorksProgress = ({ activeStep, onStepComplete }: HowItWorksProgressProps) => (
    <div
        className="w-full max-w-[280px] sm:max-w-[360px] md:max-w-[420px] lg:max-w-[527px]"
        style={{ containerType: 'inline-size' }}
    >
        <div
            style={{
                // 1em == 1 unit of the original 528-wide artwork
                fontSize: `${100 / CANVAS_WIDTH}cqi`,
                padding: '31em 31em 51em',
                borderRadius: '24em',
                border: '1em solid transparent',
                // padding-box holds the fill, border-box paints the 1px gradient stroke
                background:
                    'linear-gradient(180deg, rgba(142,87,250,0.5), rgba(142,87,250,0.2)) padding-box,' +
                    'linear-gradient(131deg, rgba(142,87,250,0.3), rgba(142,87,250,0.1)) border-box',
                display: 'flex',
                flexDirection: 'column',
                gap: '24em',
            }}
        >
            {cards.map((card, index) => (
                <div
                    key={card.title}
                    className={`transition-[filter] duration-[600ms] ease-out motion-reduce:transition-none ${activeStep < index ? DIMMED : ''}`}
                    style={{
                        height: `${CARD_HEIGHT}em`,
                        padding: '24em',
                        borderRadius: '16em',
                        background: '#321E58',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {/* Icon tile */}
                        <div
                            style={{
                                width: '48em',
                                height: '48em',
                                borderRadius: '14em',
                                background: '#6B41BC',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            {card.icon}
                        </div>

                        <div style={{ marginLeft: '12em', minWidth: 0 }}>
                            <p
                                style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontWeight: 600,
                                    fontSize: '17em',
                                    lineHeight: 1.2,
                                    color: '#FFFFFF',
                                }}
                            >
                                {card.title}
                            </p>
                            <p
                                style={{
                                    fontFamily: "'Roboto', sans-serif",
                                    fontWeight: 400,
                                    fontSize: '12.5em',
                                    lineHeight: 1.2,
                                    color: '#DCCBFD',
                                }}
                            >
                                {card.subtitle}
                            </p>
                        </div>
                    </div>

                    <div style={{ height: '8em', borderRadius: '4em', background: '#DCCBFD' }}>
                        <div
                            className="transition-[width] duration-[900ms] ease-out motion-reduce:transition-none"
                            style={{
                                width: activeStep >= index ? '100%' : '0%',
                                height: '100%',
                                borderRadius: '4em',
                                background: '#6B41BC',
                                opacity: 0.789,
                            }}
                            onTransitionEnd={(e) => {
                                if (e.propertyName === 'width' && index === activeStep) onStepComplete();
                            }}
                        />
                    </div>
                </div>
            ))}
        </div>
    </div>
);

export default HowItWorksProgress;
